/**
 * Where a muscle group's weekly volume sits against its own MEV / MAV / MRV, and what the rail
 * under the canvas therefore draws.
 *
 * ── It is a WEEKLY judgement, and the TAPE reaches it by conversion (owner, 7 September 2026) ─
 * `LANDMARKS` below is a table of weekly volumes; the load handed in under the tape is one
 * self-paced CYCLE's. Comparing the two raw has no defined meaning — measured 7 September 2026,
 * every group on a tape plan read "poniżej MEV" permanently, which is a confident wrong answer
 * rather than a missing one — so the rail was withheld there for one round.
 *
 * The owner's answer is the KANON sequence sheet's own note, which already says MAV is "normalised
 * to 7 days": scale the cycle's sets to a week, `cycleSets × 7 / cycleLengthInDays`, so a shorter
 * cycle projects up and a longer one averages down and the weekly landmarks then apply honestly.
 * The unit line says the number was CONVERTED rather than counted, because a figure that looks
 * counted and is not is the defect the withholding was avoiding.
 *
 * **The cycle's length in days is no longer computed here.** It arrives as a parameter, from
 * `model/cycleDays.ts`, which is now the one place `restDaysAfter` is summed — the 07.09 sequence
 * mock prints the same figure in the pattern row and in this rail's own caption, and two derivations
 * of one number are two answers to one question. `cycleDays.ts`'s header carries the R2 argument
 * (R2 as amended 2026-09-13) in full; what matters here is that this module now only divides by a
 * number it was handed, and that `TapeProjection` still carries no `cycleLengthDays` field.
 *
 * ── It is drawn while the CATALOGUE is still loading, and it says nothing then ────────────────
 * The points are attributed through `entities/exercise-library`'s map, which is a second read. Until
 * it lands the attribution is empty, every group's load is zero, and six cells reading `0` /
 * "poniżej MEV" over a fully programmed week is the same confident wrong answer the tape used to
 * give. So `isPending` is a parameter, every data-bearing field is `null` while it is true, and the
 * rail reserves its box and stands a gated skeleton in it. `CLAUDE.md`: a section waits for all of
 * its reads.
 *
 * ── A cycle with no saveable day KEEPS its band, and says nothing in it ──────────────────────
 * This module used to return `null` at `cycleLengthDays === 0`, on the argument that no rail was the
 * honest picture. That argument is withdrawn: it made the rail's presence a function of the data,
 * and zero became reachable in ordinary editing the moment the length started counting only the days
 * the save keeps. Pressing "+ Dzień treningowy" produces a day with no exercises, the length drops
 * to 0, and the whole band unmounted — the canvas above it then grew into the space and everything
 * moved. A geometry change under a data change is the defect `CLAUDE.md`'s loading policy exists to
 * prevent, and it outranks the tidiness of not drawing a rail with nothing in it.
 *
 * The cell already has a shape for "no honest number yet" — `value`, `status` and `statusLabel`
 * `null` and no fill — and that is what an empty cycle wears. It is NOT pending: no read is open, so
 * no skeleton is gated over it and `aria-busy` stays off.
 *
 * The `Builder planu — tydzień + programator` handoff replaced the old rail's six share bars with
 * six THRESHOLD bars: a track that ends at MRV, a fill, two hairline ticks at MEV and MAV, and one
 * word saying which band the number is in. This module is that judgement and nothing else.
 *
 * ── It computes no volume. It reads one ──────────────────────────────────────────────────────
 * The points come in as a `MuscleLoad` — `entities/plan-draft`'s `muscleLoadByDay` already summed
 * them, over the rollup `MUSCLE_DISPLAY_GROUPS` names and under the counting rule `setCounting.ts`
 * owns (`countsAsWork`: a set counts unless it is a warm-up). None of that is re-derived here, and
 * a second implementation of any of it would be a second answer to "how many sets is this week".
 * What this module adds is the comparison against a table, which lives nowhere else.
 *
 * ── Two dependencies, both accepted rather than created ──────────────────────────────────────
 * `t` and `locale` arrive as parameters — `CLAUDE.md`: `model/` imports no i18next, no React, no
 * DOM. `formatPoints` is `builderCopy`'s, so "11,5" is printed by the one function that already
 * prints it on the day card.
 *
 * ── The colour is NOT here ───────────────────────────────────────────────────────────────────
 * The view model answers with a status NAME; the rail's stylesheet binds each name to a fill and
 * an ink. Two reasons: a bar needs 3:1 and a word needs 4.5:1, so the two are different tokens
 * (`--good` / `--good-ink`, `--warn` / `--warn-ink`, `--danger` / `--danger-ink`) and a model
 * returning one hex could not say both; and the mock paints "ponad MRV" in the ember, which this
 * app does not — the accent budget is four marks a screen and a bar that competes with Save is
 * spending one on a measurement. Over-MRV is `--danger`, which is what it means.
 */

import {
  MUSCLE_DISPLAY_GROUPS,
  type MuscleDisplayGroup,
  type MuscleLoad,
} from '~/entities/plan-draft';

import { KEY, formatPoints, muscleLabel, type Translate } from './builderCopy';

/**
 * The three landmarks of one group's weekly volume, in counting sets.
 *
 * MEV — minimum effective volume, the floor below which the week is unlikely to drive an
 * adaptation. MAV — maximum adaptive volume, the top of the band most weeks should sit in. MRV —
 * maximum recoverable volume, past which the week is more than the athlete can absorb.
 */
type VolumeLandmarks = readonly [mev: number, mav: number, mrv: number];

/**
 * The handoff's own `MAVL` table, transcribed.
 *
 * A `Record` over `MuscleDisplayGroup` rather than a lookup with a fallback, so a seventh display
 * group is a compile error rather than a bar that silently draws against zero. The numbers are the
 * mock's and are not this app's to tune: they are a training-science default the trainer reads
 * their own week against, and the owner has not asked for them to be authorable.
 */
const LANDMARKS: Record<MuscleDisplayGroup, VolumeLandmarks> = {
  chest: [10, 16, 22],
  back: [10, 18, 25],
  legs: [8, 16, 22],
  shoulders: [8, 16, 22],
  arms: [6, 14, 20],
  core: [6, 12, 18],
};

/**
 * Which band a group's volume is in.
 *
 * **Both boundaries are CLOSED into the band**: a week landing exactly on MEV, and one landing
 * exactly on MAV, are both `inRange`. The landmarks are the edges of the useful band rather than
 * the first value outside it, so a trainer who programmed exactly sixteen chest sets has hit the
 * top of the adaptive band, not overshot it.
 */
export type VolumeStatus = 'belowMev' | 'inRange' | 'aboveMav' | 'aboveMrv';

export function volumeStatus(points: number, [mev, mav, mrv]: VolumeLandmarks): VolumeStatus {
  if (points > mrv) return 'aboveMrv';
  if (points > mav) return 'aboveMav';
  if (points < mev) return 'belowMev';
  return 'inRange';
}

/** One muscle's cell on the rail: a dot, a name, a number, a track with two ticks, and a word. */
export interface VolumeCellView {
  readonly group: MuscleDisplayGroup;
  /** "Klatka" — the trainer's word, from the six display labels `builderCopy` owns. */
  readonly label: string;
  /**
   * Absolute counting sets, formatted for the locale: `11,5` in Polish — and on the tape, the
   * week-equivalent the conversion produced.
   *
   * `null` while the exercise catalogue is still in flight, because there is no honest number to
   * print yet. The cell keeps its box and its name; only the ink waits.
   */
  readonly value: string | null;
  /** `null` for the same reason `value` is. */
  readonly status: VolumeStatus | null;
  /** "w zakresie" — the band, in words, because a band carried by colour alone is not carried. */
  readonly statusLabel: string | null;
  /**
   * How much of the track the fill covers, 0–100. The track ENDS at MRV, so this is
   * `points / MRV`, clamped — a week past MRV fills the bar and says so in the word beside it
   * rather than overflowing its own box. `0` while the read is in flight, where the rail draws no
   * fill at all.
   */
  readonly fillPercent: number;
  /** Where the MEV hairline stands, 0–100 of the track. */
  readonly mevPercent: number;
  /** Where the MAV hairline stands. Always above `mevPercent`, and below 100 on every group. */
  readonly mavPercent: number;
}

/** The rail's caption block and its six cells. */
export interface VolumeRailView {
  /** "Objętość vs MAV". */
  readonly title: string;
  /** "MEV → MAV → MRV" — what the two ticks and the track's end are. */
  readonly scale: string;
  /**
   * "serie tygodniowo" on the week; "na 7 dni · cykl 5 dni" on the sequence canvas, where the
   * number was converted rather than counted — see the header. The cycle's length is interpolated
   * from the SAME figure the pattern row prints, so the two cannot disagree.
   */
  readonly unit: string;
  /* ── The 1,0 / 0,5 weighting is stated NOWHERE on this screen, and that is recorded ────────
   * It was a printed caption under the old share bars, cut with them on 7 September 2026, and then
   * put behind an ⓘ on the rail's caption. The 07.09 programator mock draws no ⓘ, and the owner's
   * instruction of 8 September 2026 is that nothing outside the mock is visible — so `hint` and
   * `hintLabel` are gone with the popover that read them, and a trainer reading "11,5" has no way
   * to learn that a secondary muscle counts half. It is an open item for the owner, not a thing to
   * smuggle onto another surface. The COPY is still in `web/{pl,en}.json`, so restoring it is one
   * component away. */
  /** Announced while the catalogue read is in flight, so the wait is not silent. */
  readonly pendingLabel: string;
  /** True while the exercise catalogue is still loading — see the header and `VolumeCellView`. */
  readonly isPending: boolean;
  /** All six groups, heaviest first. A group the phase does not train draws at zero, and says so. */
  readonly cells: readonly VolumeCellView[];
}

/** What `toVolumeRail` needs beyond the load itself. */
export interface VolumeRailInput {
  /** The exercise catalogue read has not answered yet, so no cell may state a number. */
  readonly isPending: boolean;
  /**
   * How many days the phase's cycle runs, from `model/cycleDays.ts` — or `null` on the WEEK, where
   * the load already is a week's and there is nothing to convert.
   *
   * `0` is a third, distinct answer: a sequence phase whose every day would be dropped by the save.
   * It is not the same as `null` — there is no cycle to divide by, so no cell may state a number —
   * but it is also not a reason to withhold the BAND. See the header.
   */
  readonly cycleLengthDays: number | null;
}

/**
 * The rail, from one scope's muscle load.
 *
 * **Six cells always**, projected over `MUSCLE_DISPLAY_GROUPS` rather than over `load.entries`: a
 * group the phase never trains is exactly the finding the rail exists to surface, and a scope that
 * omitted its zeroes would draw five cells and quietly hide the sixth. The week's own breakdown
 * already carries all six; asking the canonical list is what makes that true of any scope handed in.
 *
 * **Sorted by volume, descending**, which is the mock's own order — the heaviest group is the one a
 * trainer checks first. Ties fall back to canonical order, so two groups holding the same number do
 * not swap places between renders.
 */
export function toVolumeRail(
  t: Translate,
  locale: string,
  load: MuscleLoad,
  { isPending, cycleLengthDays }: VolumeRailInput,
): VolumeRailView {
  /**
   * The two states in which no cell may state a number: the catalogue has not answered, and the
   * cycle has no day the save would keep. They are different facts and the rail draws them the
   * same way, because the cell has exactly one shape for "no honest number yet".
   *
   * `isPending` is deliberately NOT folded into this. It drives the skeleton and `aria-busy`, and a
   * cycle with no saveable day is not pending — it is empty, and a skeleton over it would promise
   * an answer that is not coming.
   */
  const silent = isPending || cycleLengthDays === 0;
  // Never `7 / 0`: at a length of zero nothing is printed anyway, so the factor is inert.
  const toWeek = cycleLengthDays === null || cycleLengthDays === 0 ? 1 : 7 / cycleLengthDays;

  const pointsByGroup = new Map(load.entries.map((entry) => [entry.group, entry.points]));

  const cells = MUSCLE_DISPLAY_GROUPS.map((group, canonicalOrder): VolumeCellView & {
    readonly points: number;
    readonly canonicalOrder: number;
  } => {
    const landmarks = LANDMARKS[group];
    const [mev, mav, mrv] = landmarks;
    const points = (pointsByGroup.get(group) ?? 0) * toWeek;
    const status = volumeStatus(points, landmarks);
    return {
      group,
      canonicalOrder,
      // Sorted on the real number even while it is not printed, so the six cells do not reorder
      // themselves under the trainer the moment the catalogue lands.
      points,
      label: muscleLabel(t, group),
      value: silent ? null : formatPoints(locale, points),
      status: silent ? null : status,
      statusLabel: silent ? null : t(`${KEY}.volume.status.${status}`),
      fillPercent: silent ? 0 : Math.min(100, (points / mrv) * 100),
      mevPercent: (mev / mrv) * 100,
      mavPercent: (mav / mrv) * 100,
    };
  })
    .sort((a, b) => b.points - a.points || a.canonicalOrder - b.canonicalOrder)
    .map(({ points: _points, canonicalOrder: _canonicalOrder, ...cell }) => cell);

  return {
    title: t(`${KEY}.volume.title`),
    scale: t(`${KEY}.volume.scale`),
    unit:
      cycleLengthDays === null
        ? t(`${KEY}.volume.unit.week`)
        : t(`${KEY}.volume.unit.convertedFromCycle`, {
            length: t(`${KEY}.cycleLength`, { count: cycleLengthDays }),
          }),
    pendingLabel: t(`${KEY}.volume.pending`),
    isPending,
    cells,
  };
}
